import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/* Body-scroll lock is shared across all dialog instances. Tracking a refcount
   lets stacked dialogs (e.g. quick-add → cart drawer auto-open) share a
   single lock: only the first opening locks and captures scrollY; only the
   last closing unlocks and restores. Without this, each instance writes its
   own #previousScrollY back on close — and any dialog that opens while body
   is already locked captures scrollY=0, so the chain of closes ends with
   `window.scrollTo({ top: 0 })` and the page flies to the top. */
let lockCount = 0;
let lockedScrollY = 0;

// Minimum vertical pointer movement (px) before a click is treated as
// having followed a drag, rather than a plain tap. Below this, jitter from
// an ordinary tap shouldn't be read as a drag in either direction.
const DRAG_CLOSE_THRESHOLD_PX = 10;

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    // Read scrollY outside rAF to avoid layout thrashing. The value is only
    // applied if we're the first dialog to open; later showDialog calls while
    // another dialog is already open just bump the refcount.
    const scrollY = window.scrollY;

    requestAnimationFrame(() => {
      if (lockCount === 0) {
        lockedScrollY = scrollY;
        document.body.style.width = '100%';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
      }
      lockCount++;

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
      this.addEventListener('pointerdown', this.#handlePointerDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    this.removeEventListener('pointerdown', this.#handlePointerDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    lockCount--;
    if (lockCount === 0) {
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: lockedScrollY, behavior: 'instant' });
    }

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (!isClickedOutside(event, dialog)) return;

    // A drag/swipe that starts inside the sheet and moves upward (scrolling
    // its content, or the elastic overscroll bounce at the top of that
    // scroll on iOS) can end with a synthesized click whose target resolves
    // to the dialog's own backdrop — isClickedOutside reads that the same
    // as a genuine outside tap. Only actually close for a real tap (no
    // meaningful drag) or a drag that moved downward, so pulling the sheet
    // up never closes it — only pulling it down does.
    const dragDeltaY = this.#pointerDownY === null ? 0 : event.clientY - this.#pointerDownY;
    if (dragDeltaY < -DRAG_CLOSE_THRESHOLD_PX) return;

    this.closeDialog();
  }

  /**
   * Records where a pointer interaction started, so #handleClick can tell a
   * plain tap (or downward drag) apart from an upward drag on the eventual
   * click.
   *
   * @param {PointerEvent} event
   */
  #handlePointerDown(event) {
    this.#pointerDownY = event.clientY;
  }

  /** @type {number | null} */
  #pointerDownY = null;

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
